import { mapApiResourceToOwnerAppointment } from '@/api/appointments/appointmentMapping';
import type {
  OwnerAppointment,
  OwnerAppointmentApiResource,
} from '@/api/appointments/appointmentsApi.types';
import {
  mapApiResourceToClientAnnouncement,
  type ClientAnnouncement,
  type ClientAnnouncementApiResource,
} from '@/api/announcements/announcementsApi.types';
import {
  mapApiResourceToClinicContact,
  type ClinicContact,
  type ClinicContactApiResource,
} from '@/api/clinic/clinicApi.types';
import {
  mapApiResourceToIntakeSubmissionListItem,
  type IntakeSubmissionListItem,
  type IntakeSubmissionListItemApiResource,
} from '@/api/intake/intakeApi.types';
import {
  mapApiResourceToOwnerPet,
  type OwnerPet,
  type OwnerPetApiResource,
} from '@/api/patients/patientsApi.types';

export interface PetExercisesDueTodayApiResource {
  patient_id: string;
  patient_name: string;
  due_exercise_count: number;
}

export interface PetExercisesDueToday {
  patientId: string;
  patientName: string;
  dueExerciseCount: number;
}

export interface PortalSummaryApiResource {
  client_full_name: string;
  pets: OwnerPetApiResource[];
  next_visit: OwnerAppointmentApiResource | null;
  consents_needing_reacceptance_count: number;
  intake_submissions: IntakeSubmissionListItemApiResource[];
  pets_with_exercises_due_today: PetExercisesDueTodayApiResource[];
  unread_chat_count: number;
  latest_announcements: ClientAnnouncementApiResource[];
  total_announcements_count: number;
  clinic_contact: ClinicContactApiResource;
}

export interface PortalSummary {
  clientFullName: string;
  pets: OwnerPet[];
  nextVisit: OwnerAppointment | null;
  consentsNeedingReacceptanceCount: number;
  intakeSubmissions: IntakeSubmissionListItem[];
  petsWithExercisesDueToday: PetExercisesDueToday[];
  unreadChatCount: number;
  latestAnnouncements: ClientAnnouncement[];
  totalAnnouncementsCount: number;
  clinicContact: ClinicContact;
}

function mapApiResourceToPetExercisesDueToday(
  resource: PetExercisesDueTodayApiResource
): PetExercisesDueToday {
  return {
    patientId: resource.patient_id,
    patientName: resource.patient_name,
    dueExerciseCount: resource.due_exercise_count,
  };
}

export function mapApiResourceToPortalSummary(resource: PortalSummaryApiResource): PortalSummary {
  return {
    clientFullName: resource.client_full_name,
    pets: resource.pets.map(mapApiResourceToOwnerPet),
    nextVisit:
      resource.next_visit === null ? null : mapApiResourceToOwnerAppointment(resource.next_visit),
    consentsNeedingReacceptanceCount: resource.consents_needing_reacceptance_count,
    intakeSubmissions: resource.intake_submissions.map(mapApiResourceToIntakeSubmissionListItem),
    petsWithExercisesDueToday: resource.pets_with_exercises_due_today.map(
      mapApiResourceToPetExercisesDueToday
    ),
    unreadChatCount: resource.unread_chat_count,
    latestAnnouncements: resource.latest_announcements.map(mapApiResourceToClientAnnouncement),
    totalAnnouncementsCount: resource.total_announcements_count,
    clinicContact: mapApiResourceToClinicContact(resource.clinic_contact),
  };
}
